import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Badge } from '@/components/ui/badge';
import { Bot, Edit2, Trash2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ExecutionStatusOverlay from './ExecutionStatusOverlay';
import { getExecutionBorderClass, type ExecutionStatus } from './executionStatus';

interface AgentCallNodeData {
  agentId: string;
  agentName: string;
  agentDescription?: string;
  domain?: string;
  executionStatus?: ExecutionStatus;
  onEdit?: () => void;
  onDelete?: () => void;
  onViewAgent?: () => void;
}

function AgentCallNode({ data, selected }: NodeProps<AgentCallNodeData>) {
  return (
    <div
      className={`bg-white rounded-lg border-2 shadow-lg min-w-[280px] max-w-[320px] relative ${
        selected ? 'border-purple-500 shadow-purple-200' : 'border-purple-300'
      } ${getExecutionBorderClass(data.executionStatus)}`}
    >
      <ExecutionStatusOverlay status={data.executionStatus} />
      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 !bg-purple-500"
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-3 py-2 rounded-t-md flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bot className="w-4 h-4" />
          <span className="font-semibold text-sm">Agent Call</span>
        </div>
        <div className="flex gap-1">
          {data.onViewAgent && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 hover:bg-purple-600"
              onClick={data.onViewAgent}
            >
              <ExternalLink className="w-3 h-3" />
            </Button>
          )}
          {data.onEdit && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 hover:bg-purple-600"
              onClick={data.onEdit}
            >
              <Edit2 className="w-3 h-3" />
            </Button>
          )}
          {data.onDelete && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 hover:bg-red-600"
              onClick={data.onDelete}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-3 space-y-2">
        <div>
          <div className="font-semibold text-sm text-gray-900">{data.agentName}</div>
          {data.agentDescription && (
            <div className="text-xs text-gray-600 mt-1 line-clamp-2">
              {data.agentDescription}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-2 border-t">
          {data.domain && (
            <Badge variant="secondary" className="text-xs">
              {data.domain}
            </Badge>
          )}
          <span className="text-xs text-gray-500 font-mono">{data.agentId}</span>
        </div>
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 !bg-purple-500"
      />
    </div>
  );
}

export default memo(AgentCallNode);

